import React from "react";
import classnames from "classnames";
import { GeneralIcon, type GeneralIcons } from "modules/common/components/planr/icon/Generalcon";
import styles from "./MobileHeader.module.scss";

export interface MobileHeaderProps {
    title: string;
    /** Есть обработчик — слева показывается «Назад» (на вложенных экранах) */
    onBack?: () => void;
    /** Правая часть: обычно `HeaderIconButton` и `HeaderAvatar` */
    children?: React.ReactNode;
    className?: string;
}

/**
 * Шапка мобильной версии: слева «Назад», по центру заголовок, справа кнопки.
 * Верхний отступ — под вырез экрана (safe-area).
 */
export const MobileHeader = ({ title, onBack, children, className }: MobileHeaderProps) => (
    <header className={classnames(styles.header, className)}>
        <div className={styles.side}>
            {onBack && (
                <button type="button" className={styles.button} aria-label="Назад" onClick={onBack}>
                    <GeneralIcon type="general-chevron-left" size={24} />
                </button>
            )}
        </div>
        <h1 className={styles.title}>{title}</h1>
        <div className={classnames(styles.side, styles.right)}>{children}</div>
    </header>
);

export interface HeaderIconButtonProps {
    icon: GeneralIcons;
    /** Подпись для скринридера: кнопка без текста */
    label: string;
    onClick: () => void;
    /** Счётчик на кнопке (колокольчик); 0 или не задан — не показывается */
    counter?: number;
}

/** Круглая иконка-кнопка в шапке с местом под счётчик */
export const HeaderIconButton = ({ icon, label, onClick, counter }: HeaderIconButtonProps) => (
    <button
        type="button"
        className={styles.button}
        aria-label={counter ? `${label}: ${counter}` : label}
        onClick={onClick}
    >
        <GeneralIcon type={icon} size={24} />
        {counter && counter > 0 ? (
            <span className={styles.counter} aria-hidden="true">
                {counter > 99 ? "99+" : counter}
            </span>
        ) : null}
    </button>
);

export interface HeaderAvatarProps {
    name: string;
    /** Фото; нет — показываются инициалы */
    src?: string | null;
    onClick: () => void;
}

/** Инициалы по ФИО: первые буквы фамилии и имени */
export function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);

    return parts
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");
}

/** Аватар в шапке: ведёт в профиль и настройки */
export const HeaderAvatar = ({ name, src, onClick }: HeaderAvatarProps) => (
    <button type="button" className={styles.button} aria-label="Профиль и настройки" onClick={onClick}>
        <span className={styles.avatar}>
            {src ? <img src={src} alt="" className={styles.avatarImage} /> : initials(name) || "?"}
        </span>
    </button>
);
